import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { LocationAutocomplete } from '@/components/LocationAutocomplete/LocationAutocomplete'
import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import useMobileMediaQuery from '@/hooks/useMobileMediaQuery'
import { companyFormSchema } from '@/utils/schemas'

import type { GooglePlace } from '../utils/applyGooglePlaceToForm'
import { extractLocationParts } from '../utils/applyGooglePlaceToForm'

import { CompanyContextField } from './CompanyContextField'

type CompanyInfoFormValue = {
  companyName: string
  companyWebsite: string
  industry: string
  description: string
  countryLongName: string
  stateLongName: string
  cityLongName: string
}

export type CompanyInfoEditValue = CompanyInfoFormValue & {
  countryShortName: string
  stateShortName: string
  cityShortName: string
}

const sheetSchema = companyFormSchema.omit({ socialMedia: true })

interface CompanyInfoEditSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  value: CompanyInfoEditValue
  onSave: (next: CompanyInfoEditValue) => void
}

export const CompanyInfoEditSheet = ({
  open,
  onOpenChange,
  value,
  onSave,
}: CompanyInfoEditSheetProps) => {
  const { t } = useTranslation()
  const isMobile = useMobileMediaQuery()

  const form = useForm<CompanyInfoFormValue>({
    resolver: zodResolver(sheetSchema),
    defaultValues: {
      companyName: value.companyName,
      companyWebsite: value.companyWebsite,
      industry: value.industry,
      description: value.description ?? '',
      countryLongName: value.countryLongName,
      stateLongName: value.stateLongName,
      cityLongName: value.cityLongName,
    },
    mode: 'onChange',
  })

  const { handleSubmit, reset, setValue, control, formState } = form

  const [countryShortName, setCountryShortName] = useState(
    value.countryShortName,
  )
  const [stateShortName, setStateShortName] = useState(value.stateShortName)
  const [cityShortName, setCityShortName] = useState(value.cityShortName)

  useEffect(() => {
    if (open) {
      reset({
        companyName: value.companyName,
        companyWebsite: value.companyWebsite,
        industry: value.industry,
        description: value.description ?? '',
        countryLongName: value.countryLongName,
        stateLongName: value.stateLongName,
        cityLongName: value.cityLongName,
      })
      setCountryShortName(value.countryShortName)
      setStateShortName(value.stateShortName)
      setCityShortName(value.cityShortName)
    }
  }, [open, value, reset])

  const handleCancel = () => onOpenChange(false)
  const handleSave = handleSubmit((data) => {
    onSave({
      ...data,
      countryShortName,
      stateShortName,
      cityShortName,
    })
    onOpenChange(false)
  })

  const handlePlaceSelected = (place: GooglePlace) => {
    const parts = extractLocationParts(place)
    setValue('countryLongName', parts.countryLong, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue('stateLongName', parts.stateLong, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue('cityLongName', parts.cityLong, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setCountryShortName(parts.countryShort)
    setStateShortName(parts.stateShort)
    setCityShortName(parts.cityShort)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        className="flex flex-col gap-4 max-md:h-[85vh] max-md:rounded-t-2xl sm:max-w-md"
      >
        <SheetHeader className="text-center sm:text-center">
          <SheetTitle>
            {t('dashboard.runReport.submitReport.companyInfoSheetTitle')}
          </SheetTitle>
        </SheetHeader>

        <FormProvider {...form}>
          <form
            onSubmit={handleSave}
            className="flex flex-1 min-h-0 flex-col gap-3 overflow-y-auto"
          >
            <FormField
              control={control}
              name="companyName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    {t('dashboard.runReport.companyInfoFields.companyName')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t(
                        'dashboard.runReport.companyInfoFields.companyNamePlaceholder',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="companyWebsite"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    {t('dashboard.runReport.companyInfoFields.companyWebsite')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t(
                        'dashboard.runReport.companyInfoFields.companyWebsitePlaceholder',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="industry"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    {t('dashboard.runReport.companyInfoFields.industry')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t(
                        'dashboard.runReport.companyInfoFields.industryPlaceholder',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="countryLongName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    {t('dashboard.runReport.companyInfoFields.location')}
                  </FormLabel>
                  <FormControl>
                    <LocationAutocomplete
                      onPlaceSelected={handlePlaceSelected}
                      defaultValue={field.value}
                      placeholder={t(
                        'dashboard.runReport.companyInfoFields.locationPlaceholder',
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <CompanyContextField />
          </form>
        </FormProvider>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={handleCancel}>
            {t('dashboard.runReport.submitReport.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!formState.isValid}
          >
            {t('dashboard.runReport.submitReport.confirm')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
